import React, { useMemo } from "react";
import { formatHeatmapHourLabel } from "../../services/peopleDistributionService";
import {
  formatHeatmapHourIntervalLabel,
  formatSecondsShort,
  type HourlyHeatmapRow,
} from "../../services/peopleDistributionTypes";

type HourlyPeakHeatmapProps = {
  rows: HourlyHeatmapRow[];
  hours: string[];
};

const COLOR_SCALES = {
  blue: ["#DBEAFE", "#93C5FD", "#3B82F6", "#1D4ED8"],
  purple: ["#EDE9FE", "#C4B5FD", "#8B5CF6", "#6D28D9"],
} as const;

const TEXT_COLORS = {
  blue: ["#1E3A8A", "#1E40AF", "#EFF6FF", "#FFFFFF"],
  purple: ["#4C1D95", "#5B21B6", "#F5F3FF", "#FFFFFF"],
} as const;

function getHeatCellStyle(
  value: number,
  max: number,
  color: HourlyHeatmapRow["color"]
): { backgroundColor: string; color: string } {
  if (value <= 0 || max <= 0) {
    return { backgroundColor: COLOR_SCALES[color][0], color: TEXT_COLORS[color][0] };
  }
  const ratio = value / max;
  let tier: 1 | 2 | 3 = 1;
  if (ratio >= 0.67) tier = 3;
  else if (ratio >= 0.34) tier = 2;
  return {
    backgroundColor: COLOR_SCALES[color][tier],
    color: TEXT_COLORS[color][tier],
  };
}

function formatHeatmapCellValue(value: number, metric: HourlyHeatmapRow["metric"]): string {
  const normalized = value ?? 0;
  if (metric === "peakDwell") {
    return formatSecondsShort(normalized);
  }
  return String(normalized);
}

function formatHeatmapCellTooltip(
  rowLabel: string,
  hour: string,
  value: number,
  metric: HourlyHeatmapRow["metric"]
): string {
  const interval = formatHeatmapHourIntervalLabel(hour);
  const display = formatHeatmapCellValue(value, metric);
  return `${rowLabel} · ${interval} · ${display}`;
}

function formatHeatmapRowLabel(row: HourlyHeatmapRow): string {
  if (row.metric === "peakDwell") {
    return "Peak dwell (s)";
  }
  return row.label;
}

export const HourlyPeakHeatmap: React.FC<HourlyPeakHeatmapProps> = ({ rows, hours }) => {
  const rowMaxes = useMemo(
    () => rows.map((row) => Math.max(...row.values, 0)),
    [rows]
  );

  if (!rows.length || !hours.length) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No hourly heatmap data available.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Hourly peak heatmap</h3>
        <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
          {hours.length}h
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <div
            className="mb-2 grid gap-1"
            style={{ gridTemplateColumns: `88px repeat(${hours.length}, minmax(48px, 1fr))` }}
          >
            <div />
            {hours.map((hour) => (
              <div
                key={hour}
                className="text-center text-[10px] font-medium leading-tight text-slate-500 dark:text-slate-400"
                title={formatHeatmapHourIntervalLabel(hour)}
              >
                {formatHeatmapHourLabel(hour)}
              </div>
            ))}
          </div>

          {rows.map((row, rowIndex) => (
            <div
              key={row.metric}
              className="mb-1.5 grid items-center gap-1"
              style={{ gridTemplateColumns: `88px repeat(${hours.length}, minmax(48px, 1fr))` }}
            >
              <div className="pr-2 text-right text-[10px] font-medium text-slate-600 dark:text-slate-300">
                {formatHeatmapRowLabel(row)}
              </div>
              {row.values.map((value, colIndex) => {
                const cellStyle = getHeatCellStyle(value, rowMaxes[rowIndex], row.color);
                return (
                  <div
                    key={`${row.metric}-${hours[colIndex]}`}
                    title={formatHeatmapCellTooltip(
                      formatHeatmapRowLabel(row),
                      hours[colIndex],
                      value,
                      row.metric
                    )}
                    className="flex h-9 items-center justify-center rounded-md px-0.5 text-[10px] font-semibold leading-tight"
                    style={cellStyle}
                  >
                    {formatHeatmapCellValue(value, row.metric)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
          Peak / Avg occ
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-violet-500" />
          Peak dwell (seconds)
        </span>
      </div>
    </div>
  );
};

export default HourlyPeakHeatmap;
